<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * A modal dialog.
 *
 * Focus moves in on open and returns to where it came from on close, `Escape` closes, and focus
 * cannot tab out of it. This is not polish: the re-auth prompt is a modal, and a dialog that asks
 * for a password while letting focus wander behind it is a dialog people learn to type into
 * without reading.
 */

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    subtitle?: string
    size?: 'sm' | 'md' | 'lg'
    /** A dialog mid-request must not vanish under the person who is waiting for it. */
    busy?: boolean
    /** `false` for prompts that must be answered rather than dismissed by accident. */
    dismissible?: boolean
  }>(),
  { size: 'md', busy: false, dismissible: true },
)

const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()

const panel = ref<HTMLElement | null>(null)
let previouslyFocused: HTMLElement | null = null

const close = (): void => {
  if (props.busy || !props.dismissible) return
  emit('close')
}

const focusables = (): HTMLElement[] => {
  if (!panel.value) return []
  return [
    ...panel.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ]
}

const onKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') {
    event.stopPropagation()
    close()
    return
  }

  if (event.key !== 'Tab') return

  const items = focusables()
  if (items.length === 0) {
    event.preventDefault()
    return
  }

  const first = items[0]
  const last = items[items.length - 1]
  const active = document.activeElement as HTMLElement | null

  if (event.shiftKey && (active === first || !panel.value?.contains(active))) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      previouslyFocused = document.activeElement as HTMLElement | null
      document.body.style.overflow = 'hidden'
      await nextTick()
      ;(focusables()[0] ?? panel.value)?.focus()
    } else {
      document.body.style.overflow = ''
      previouslyFocused?.focus?.()
      previouslyFocused = null
    }
  },
)

onBeforeUnmount(() => {
  document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="scrim" @click.self="close" @keydown="onKeydown">
      <div
        ref="panel"
        class="panel"
        :class="`s-${size}`"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        tabindex="-1"
      >
        <header class="head">
          <div class="titles">
            <h2>{{ title }}</h2>
            <p v-if="subtitle" class="subtitle">{{ subtitle }}</p>
          </div>
          <button
            v-if="dismissible"
            type="button"
            class="close"
            :aria-label="t('common.close')"
            :disabled="busy"
            @click="close"
          >
            <svg viewBox="0 0 14 14" aria-hidden="true">
              <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.6" fill="none" />
            </svg>
          </button>
        </header>

        <div class="body"><slot /></div>

        <footer v-if="$slots.footer" class="foot"><slot name="footer" /></footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  background: var(--bg-overlay);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: var(--space-8) var(--space-4);
  overflow-y: auto;
}

.panel {
  background: var(--bg-surface);
  border: var(--border-width) solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  width: 100%;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - var(--space-16));
  animation: rise var(--duration-base) var(--ease-out);
}

.s-sm {
  max-width: 24rem;
}
.s-md {
  max-width: 34rem;
}
.s-lg {
  max-width: 52rem;
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
}

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-4) var(--space-3);
  border-bottom: var(--border-width) solid var(--border-subtle);
}

.titles h2 {
  font-size: var(--text-md);
}

.subtitle {
  font-size: var(--text-xs);
  color: var(--text-muted);
  margin-top: var(--space-1);
}

.close {
  background: none;
  border: 0;
  color: var(--text-muted);
  cursor: pointer;
  padding: var(--space-1);
  border-radius: var(--radius-sm);
  flex: none;
}

.close:hover:not(:disabled) {
  background: var(--bg-hover);
  color: var(--text-strong);
}

.close svg {
  width: 0.875rem;
  height: 0.875rem;
}

.body {
  padding: var(--space-4);
  overflow-y: auto;
}

.foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-top: var(--border-width) solid var(--border-subtle);
  background: var(--bg-surface-sunken);
  flex-wrap: wrap;
}
</style>
